import { FormatInlineBlocks, SplitNestedSections } from '../../internal/Helpers';
import { Localized } from '../../internal/Localized';
import { useLobbySettings } from '../../providers/LobbySettingsProvider';
import Styles from './About.module.css';
import { Button, Label } from '@app/engine';
import { FormatLocalized } from '@shared/common';
import { ReactNode, useState } from 'react';

/** File-local constants for HelpTab. */
const localConstants = {
  // Toggle glyphs mirror the dashboard InfoPanel help panels ("?" collapsed, up arrow expanded) so help reads identically across the app.
  collapsedGlyph: '?',
  expandedGlyph: '\u2191',
  // Style classes indexed by inline-block marker number, applied by FormatInlineBlocks: {0} sub-header, {1} sub-pill, {2} sub-sub-header, {3} highlight.
  answerBlockClasses: [Styles.faqAnswerSubHeader, Styles.faqAnswerSubPill, Styles.faqAnswerSubSubHeader, Styles.faqAnswerHighlight]
} as const;

/** A single FAQ entry. */
interface IFaqItem {
  question: string;
  answer: string;
}

/** Props for a collapsible FAQ block. */
interface IFaqBlockDTO {
  title: string;
  body: ReactNode;
  nested?: boolean; // Nested blocks render as indented, slightly shaded sub-panels inside a parent FAQ answer
}

/**
 * A collapsible FAQ block, mirroring the dashboard's InfoPanel collapse pattern: the header strip is the toggle target, the round button on the right shows "?" collapsed and an up arrow
 * expanded, and the body starts collapsed and smoothly expands on click. Nested blocks are the same component one level down (sub-header-marked sections inside an answer), styled with a slight
 * indent and shade variation.
 *
 * @param props - FAQ block configuration.
 * @returns The FAQ block element.
 */
function FaqBlock(props: IFaqBlockDTO) {
  const { title, body, nested } = props;
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((prev) => !prev);
  return (
    <div className={nested ? Styles.faqNested : Styles.bubblePanel}>
      {/* The whole header strip is the toggle target (matching the round button), so clicking the title expands/collapses the block too. The button carries no own handler: its native click
          bubbles to this row, keeping a single toggle per click and preserving keyboard activation. */}
      <div className={Styles.faqHeader} onClick={toggle}>
        <Label text={title} classNameContainer={nested ? Styles.faqNestedTitle : Styles.faqQuestion} />
        <Button text={open ? localConstants.expandedGlyph : localConstants.collapsedGlyph} title={open ? Localized.SharedCollapse : Localized.SharedExpand} classNameButtonEnabled={Styles.faqToggle} />
      </div>
      <div className={`${Styles.faqAnswer} ${nested ? Styles.faqNestedBody : ''} ${open ? '' : Styles.faqCollapsed}`}>{body}</div>
    </div>
  );
}

/**
 * Help / FAQ tab — multiple collapsible bubble panels, one per question, all starting collapsed. Answers whose blank-line-separated chunks carry `{0}...{0}` sub-header markers render those
 * chunks as nested collapsible sub-panels, with the answer's head and tail paragraphs staying as parent content (see SplitNestedSections).
 *
 * @returns The help tab element.
 */
export function HelpTab() {
  // Support contact email from global settings, interpolated into the answers that route custom-plan questions to the team.
  const { contactEmail } = useLobbySettings();
  const faqs: IFaqItem[] = [
    { question: Localized.AboutHelpForwardingQ, answer: Localized.AboutHelpForwardingA },
    { question: Localized.AboutHelpNumberQ, answer: Localized.AboutHelpNumberA },
    { question: Localized.AboutHelpTrialQ, answer: Localized.AboutHelpTrialA },
    { question: Localized.AboutHelpCancelQ, answer: Localized.AboutHelpCancelA },
    { question: Localized.AboutHelpLanguagesQ, answer: Localized.AboutHelpLanguagesA },
    { question: Localized.AboutHelpAICustomQ, answer: Localized.AboutHelpAICustomA },
    { question: Localized.AboutHelpPricingQ, answer: FormatLocalized(Localized.AboutHelpPricingA, contactEmail) },
    { question: Localized.AboutHelpConcurrentQ, answer: FormatLocalized(Localized.AboutHelpConcurrentA, contactEmail) },
    { question: Localized.AboutHelpAITransparencyQ, answer: Localized.AboutHelpAITransparencyA },
    { question: Localized.AboutHelpUnknownQ, answer: Localized.AboutHelpUnknownA },
    { question: Localized.AboutHelpBookingQ, answer: Localized.AboutHelpBookingA },
    { question: Localized.AboutHelpTextingQ, answer: Localized.AboutHelpTextingA }
  ];

  return (
    <section>
      <Label text={Localized.AboutHelpLabel} classNameContainer={Styles.sectionLabel} />
      <Label text={Localized.AboutHelpTitle} classNameContainer={Styles.sectionTitle} />

      <div className={Styles.faqStack}>
        {faqs.map((faq, index) => {
          const parsed = SplitNestedSections(faq.answer);
          return (
            <FaqBlock
              key={index}
              title={faq.question}
              body={
                <>
                  {parsed.head ? FormatInlineBlocks(parsed.head, localConstants.answerBlockClasses) : null}
                  {parsed.sections.length > 0 ? (
                    <div className={Styles.faqNestedGroup}>
                      {parsed.sections.map((section) => (
                        <FaqBlock key={section.title} nested title={section.title} body={FormatInlineBlocks(section.body, localConstants.answerBlockClasses)} />
                      ))}
                    </div>
                  ) : null}
                  {parsed.tail ? FormatInlineBlocks(parsed.tail, localConstants.answerBlockClasses) : null}
                </>
              }
            />
          );
        })}
      </div>
    </section>
  );
}
